import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../lib/api";
import { ChatBubble, Chip, DsButton } from "../ds";
import { DecisionDialog } from "../components/DecisionDialog";
import { PageErrorBoundary } from "../components/PageErrorBoundary";
import { toast } from "sonner";
import { Archive, ArrowDown } from "lucide-react";
import { fmtDateTime, dubaiDayKey, DaySeparator } from "../lib/time";

function ChatInner() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decision, setDecision] = useState("approve");
  const [activeId, setActiveId] = useState(null);
  const [showNewPill, setShowNewPill] = useState(false);
  const taRef = useRef(null);
  const scrollRef = useRef(null);
  const composingRef = useRef(false);
  const stickToBottomRef = useRef(true);
  const lastCountRef = useRef(0);

  // Composer auto-grow (2 → 10 rows / 40vh).
  const resize = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
    const maxFromRows = lineHeight * 10 + 16;
    const maxFromViewport = window.innerHeight * 0.4;
    const max = Math.min(maxFromRows, maxFromViewport);
    el.style.height = Math.min(el.scrollHeight, max) + "px";
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  };
  useEffect(resize, [draft]);

  const { data, isLoading } = useQuery({
    queryKey: ["chat"],
    queryFn: async () => (await api.get("/messages")).data,
    refetchInterval: 5000,
  });

  const messages = data?.messages || [];
  const tasksMeta = data?.tasks || {};

  const grouped = useMemo(() => {
    const out = []; let lastKey = null;
    for (const m of messages) {
      const k = dubaiDayKey(m.created_at);
      if (k !== lastKey) { out.push({ type: "sep", key: `sep-${k}-${out.length}`, dayKey: k }); lastKey = k; }
      out.push({ type: "msg", key: m.id, msg: m });
    }
    return out;
  }, [messages]);

  const scrollToBottom = (smooth = false) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    setShowNewPill(false);
    stickToBottomRef.current = true;
  };

  // On message count change: if user is pinned to bottom → autoscroll.
  // If they've scrolled up → show a "New messages" pill when new items arrive.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const grew = grouped.length > lastCountRef.current;
    lastCountRef.current = grouped.length;
    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
      setShowNewPill(false);
    } else if (grew) {
      setShowNewPill(true);
    }
  }, [grouped.length]);

  // First load: scroll to bottom once we have content.
  useEffect(() => {
    if (!isLoading && grouped.length > 0) {
      scrollToBottom(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    stickToBottomRef.current = atBottom;
    if (atBottom) setShowNewPill(false);
  };

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setSending(true);
    try {
      await api.post("/messages", { text });
      setDraft("");
      stickToBottomRef.current = true;
      setShowNewPill(false);
      requestAnimationFrame(resize);
      // Refetch and let the autoscroll effect handle positioning.
      await qc.invalidateQueries({ queryKey: ["chat"] });
      qc.invalidateQueries({ queryKey: ["chat-unread"] });
      requestAnimationFrame(() => scrollToBottom(false));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };
  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing && !composingRef.current) {
      e.preventDefault();
      send();
    }
  };
  const trigger = (id, d) => { setActiveId(id); setDecision(d); setDecisionOpen(true); };

  return (
    // h-screen + overflow-hidden on the root locks the page to the viewport —
    // only the message list scrolls, the composer stays pinned at the bottom.
    <div
      className="h-screen overflow-hidden flex flex-col"
      data-testid="chat-page-root"
    >
      {/* Header strip (compact so the message area gets maximum height) */}
      <div className="px-4 sm:px-6 lg:px-10 pt-4 sm:pt-6 shrink-0">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Chat</h1>
        <p className="text-xs text-muted-foreground font-mono mt-0.5">
          You talk to Chief of Staff — it routes the work
        </p>
      </div>

      {/* Message list (bounded, scrolls internally) */}
      <div className="flex-1 min-h-0 relative px-4 sm:px-6 lg:px-10 pt-3">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="h-full overflow-y-auto border border-[color:var(--line)] bg-[color:var(--panel)] rounded-[8px] p-4 space-y-3"
          data-testid="chat-messages"
        >
          <div
            className="flex items-start gap-2 rounded-md bg-[color:var(--panel-2)] border border-[color:var(--line)] px-3 py-2"
            data-testid="chat-archive-note"
          >
            <Archive className="h-3.5 w-3.5 text-[color:var(--muted)] shrink-0 mt-0.5" />
            <p className="font-mono text-[10.5px] text-[color:var(--muted)] leading-snug">
              Showing the last 48 hours. Older messages are archived — ask Chief of Staff for a chat summary of a date.
            </p>
          </div>

          {isLoading && <p className="text-sm text-[color:var(--muted)]">Loading…</p>}
          {!isLoading && grouped.length === 0 && (
            <p className="text-sm text-[color:var(--muted)] text-center py-10">
              No messages in the last 48 hours.
            </p>
          )}

          {grouped.map((item) => {
            if (item.type === "sep") return <DaySeparator key={item.key} dayKey={item.dayKey} />;
            const m = item.msg;
            const isCeo = m.sender_type === "ceo";
            return (
              <div key={m.id} data-testid={`chat-msg-${m.id}`}>
                <ChatBubble
                  isCeo={isCeo}
                  agent={!isCeo ? { name: m.agent_name || "Chief of Staff", department: "CEO Office" } : null}
                  name={m.agent_name || "Chief of Staff"}
                  time={fmtDateTime(m.created_at)}
                  meta={
                    (m.task_ids || []).length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {(m.task_ids || []).map((tid) => {
                          const t = tasksMeta[tid];
                          if (!t) return null;
                          const canDecide = t.state === "plan_ready";
                          return (
                            <div key={tid} className="flex items-center gap-2 flex-wrap" data-testid={`chat-task-chip-${t.id}`}>
                              <Link to={`/tasks/${t.id}`} className="whitespace-nowrap">
                                <Chip value={t.id.slice(-4)} label={`T-${t.id.slice(-4)}`} />
                              </Link>
                              <Chip value={t.state} />
                              {canDecide && (
                                <span className="flex gap-1">
                                  <DsButton variant="go" onClick={() => trigger(t.id, "approve")} data-testid={`chat-approve-${t.id}`}>Approve</DsButton>
                                  <DsButton variant="default" onClick={() => trigger(t.id, "changes")} data-testid={`chat-changes-${t.id}`}>Changes</DsButton>
                                  <DsButton variant="no" onClick={() => trigger(t.id, "reject")} data-testid={`chat-reject-${t.id}`}>Reject</DsButton>
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )
                  }
                >
                  {m.text}
                </ChatBubble>
              </div>
            );
          })}
        </div>

        {/* "New messages" pill — shown only when the user is scrolled up and
            new content arrived. Clicking it jumps to the newest message. */}
        {showNewPill && (
          <button
            onClick={() => scrollToBottom(true)}
            data-testid="chat-new-messages-pill"
            className="absolute bottom-5 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 rounded-full bg-[color:var(--cobalt)] text-white px-4 py-1.5 text-xs font-mono shadow-lg shadow-[color:var(--cobalt)]/30 hover:brightness-110"
          >
            <ArrowDown className="h-3.5 w-3.5" /> New messages
          </button>
        )}
      </div>

      {/* Composer — pinned to the bottom of the viewport */}
      <div
        className="shrink-0 px-4 sm:px-6 lg:px-10 py-3 border-t border-[color:var(--line)] bg-[color:var(--bg)]"
        data-testid="chat-input-bar"
      >
        <div className="flex items-start gap-3 border border-[color:var(--line)] bg-[color:var(--panel)] rounded-[8px] p-3">
          <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--muted)] shrink-0 mt-1.5">YOU</span>
          <div className="flex-1 min-w-0 flex flex-col gap-1">
            <textarea
              ref={taRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKey}
              onCompositionStart={() => { composingRef.current = true; }}
              onCompositionEnd={() => { composingRef.current = false; }}
              placeholder="Write to Chief of Staff…"
              rows={2}
              data-testid="chat-input"
              className="w-full bg-transparent outline-none resize-none text-sm text-[color:var(--fg)] placeholder:text-[color:var(--muted)] leading-5"
            />
            <p className="font-mono text-[10px] text-[color:var(--muted)] select-none" data-testid="chat-input-hint">
              Enter to send · Shift+Enter for new line
            </p>
          </div>
          <DsButton variant="primary" onClick={send} disabled={sending || !draft.trim()} data-testid="chat-send">
            Send
          </DsButton>
        </div>
      </div>

      <DecisionDialog
        open={decisionOpen}
        onOpenChange={setDecisionOpen}
        taskId={activeId}
        decision={decision}
        onDone={() => qc.invalidateQueries({ queryKey: ["chat"] })}
      />
    </div>
  );
}

export default function Chat() {
  return (
    <PageErrorBoundary>
      <ChatInner />
    </PageErrorBoundary>
  );
}
